/**
 * Tâche 1265 (Eric 2026-06-28) — « Termes souvent corrigés ».
 *
 * Chaque correction gardée dans une transcription (passe jargon, passe langue,
 * correction à la main) est une trace : si le même mot est corrigé deux fois
 * ou plus, ce n'est pas une faute de frappe, c'est un terme métier qui manque
 * au dictionnaire. Le panneau les remonte, les plus fréquents d'abord, et le
 * bouton ouvre le formulaire PRÉ-REMPLI pour que la définition soit écrite à
 * la main (choix d'Eric : rien n'entre au dictionnaire sans définition).
 *
 * Les termes déjà présents au dictionnaire — terme ou alias — sont exclus côté
 * serveur : sans cela le panneau aurait proposé sans fin ce qui existe déjà.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export const MisspellingCandidates = ({ onAdd, refreshKey = 0 }) => {
  const { tr } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/api/jargon/misspelling-candidates?min_occurrences=2`, { withCredentials: true })
      .then(({ data: res }) => { if (alive) setData(res); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshKey]);

  const candidates = data?.candidates || [];
  if (loading && !data) return null;
  if (!candidates.length) return null;

  return (
    <div
      className="mb-4 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2"
      data-testid="jargon-misspellings-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="jargon-misspellings-toggle"
      >
        <span className="text-sm font-semibold text-amber-900">
          {tr("Termes souvent corrigés dans les transcriptions", "Terms often corrected in transcripts")}
          <span
            className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold tabular-nums"
            data-testid="jargon-misspellings-count"
          >
            {candidates.length}
          </span>
        </span>
        <span className="text-xs text-amber-800">{open ? tr("masquer", "hide") : tr("voir", "show")}</span>
      </button>
      {open && (
        <ul className="mt-2 flex flex-col gap-1" data-testid="jargon-misspellings-list">
          {candidates.map((c, i) => (
            <li
              key={`${c.wrong}-${c.correction}`}
              className="flex items-center gap-2 flex-wrap text-xs bg-white border border-amber-200 rounded px-2 py-1.5"
              data-testid={`jargon-misspelling-row-${i}`}
            >
              <span className="font-mono text-zinc-500 line-through">{c.wrong}</span>
              <span className="text-amber-800">→</span>
              <span className="font-semibold text-zinc-900">{c.correction}</span>
              <span className="text-[11px] text-zinc-500">
                {tr(
                  `corrigé ${c.occurrences} fois dans ${c.videos} vidéo(s)`,
                  `corrected ${c.occurrences} times across ${c.videos} video(s)`,
                )}
                {c.reasons?.length ? ` · ${c.reasons.join(", ")}` : ""}
              </span>
              <button
                type="button"
                onClick={() => onAdd(c.correction)}
                className="ml-auto text-[11px] font-semibold px-2 py-1 rounded bg-[#002FA7] text-white hover:bg-[#002280]"
                data-testid={`jargon-misspelling-add-${i}`}
                title={tr("Ouvre le formulaire pré-rempli : la définition reste à écrire", "Opens the pre-filled form: the definition is still yours to write")}
              >
                {tr("Ajouter au dictionnaire", "Add to the dictionary")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MisspellingCandidates;
